import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, 
  faArrowLeft, 
  faTimes, 
  faInfoCircle,
  faUpload,
  faFile,
  faPlus,
  faTrash,
  faLayerGroup
} from '@fortawesome/free-solid-svg-icons';
import adminService from '../../services/adminService';
import { ADMIN_URLS } from '../../config/apiConfig';

function AddResource() {
  const navigate = useNavigate();
  
  // State for single resource mode
  const [formData, setFormData] = useState({
    resource_type: 'document',
    url: '',
    is_public: true,
    program_id: ''
  });
  
  // State for batch upload mode
  const [isBatchMode, setIsBatchMode] = useState(false);
  const [batchFormData, setBatchFormData] = useState({
    resource_type: 'document',
    is_public: true,
    program_id: ''
  });
  const [batchFiles, setBatchFiles] = useState([]);
  
  const [errors, setErrors] = useState({});
  const [batchErrors, setBatchErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [uploadResults, setUploadResults] = useState([]);
  const [showResults, setShowResults] = useState(false);

  // File validation constants
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
  const ALLOWED_FILE_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/gif',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.ms-powerpoint',
    'text/plain',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel'
  ];

  // Helper function to validate file
  const validateFile = (file) => {
    if (file.size > MAX_FILE_SIZE) {
      return 'File size exceeds 10MB limit. Please select a smaller file.';
    }
    
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return 'Invalid file type. Please upload PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX, TXT, or image files.';
    }
    
    return null;
  };

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  useEffect(() => {
    // Fetch programs for dropdown
    const fetchPrograms = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${ADMIN_URLS.PROGRAMS}?status=completed`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch programs');
        }
        
        const data = await response.json();
        setPrograms(data.items || []);
      } catch (err) {
        console.error("Error fetching programs:", err);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPrograms();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    // Update the appropriate form data based on mode
    if (isBatchMode) {
      setBatchFormData({
        ...batchFormData,
        [name]: newValue
      });
    } else {
      setFormData({
        ...formData,
        [name]: newValue
      });
    }
    
    // Clear error when field is changed
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const error = validateFile(selectedFile);
      if (error) {
        setFileError(error);
        setFile(null);
      } else {
        setFileError(null);
        setFile(selectedFile);
      }
    }
  };
  
  const handleBatchFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    const validFiles = [];
    const errors = [];
    
    newFiles.forEach((file, index) => {
      const error = validateFile(file);
      if (error) {
        errors.push({ index, name: file.name, error: error });
      } else {
        validFiles.push(file);
      }
    });
    
    setBatchFiles([...batchFiles, ...validFiles]);
    setBatchErrors(errors);
  };
  
  const removeBatchFile = (indexToRemove) => {
    setBatchFiles(batchFiles.filter((_, index) => index !== indexToRemove));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.resource_type) {
      newErrors.resource_type = 'Resource type is required';
    }
    
    if (!formData.url && !file) {
      newErrors.url = 'Either provide a URL or upload a file';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const validateBatchForm = () => {
    const newErrors = {};
    
    if (!batchFormData.resource_type) {
      newErrors.resource_type = 'Resource type is required';
    }
    
    if (batchFiles.length === 0) {
      newErrors.files = 'Please select at least one file to upload';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const toggleMode = () => {
    setIsBatchMode(!isBatchMode);
    // Reset errors when switching modes
    setErrors({});
    setBatchErrors([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isBatchMode) {
      if (!validateBatchForm()) {
        return;
      }
      await handleBatchSubmit();
    } else {
      if (!validateForm()) {
        return;
      }
      await handleSingleSubmit();
    }
  };
  
  const handleSingleSubmit = async () => {
    setIsSaving(true);
    try {
      if (file) {
        // If there's a file to upload, use FormData
        const formDataToSend = new FormData();
        formDataToSend.append('file', file);
        formDataToSend.append('resource_type', formData.resource_type);
        formDataToSend.append('is_public', formData.is_public.toString());
        if (formData.program_id) {
          formDataToSend.append('program_id', formData.program_id);
        }
        
        // Custom file upload request
        const response = await fetch(ADMIN_URLS.RESOURCES, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          },
          body: formDataToSend
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          
          // Handle specific error codes
          if (response.status === 413) {
            throw new Error('File size exceeds 10MB limit. Please select a smaller file.');
          } else if (response.status === 400) {
            throw new Error(errorData.error || 'Invalid file or data. Please check your input and try again.');
          } else if (response.status === 500) {
            throw new Error('Server error. Please try again later.');
          } else {
            throw new Error(errorData.error || `Server error (${response.status}). Please try again.`);
          }
        }
        
        const result = await response.json();
        navigate(`/admin/resources/${result.id}`);
      } else {
        // Standard JSON create without file
        const result = await adminService.createResource(formData);
        navigate(`/admin/resources/${result.id}`);
      }
    } catch (err) {
      console.error("Error creating resource:", err);
      setErrors({
        ...errors,
        submit: err.message || 'Failed to create resource. Please try again.'
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleBatchSubmit = async () => {
    setIsSaving(true);
    setUploadResults([]);
    const results = [];
    let hasError = false;
    
    try {
      // Upload each file sequentially and track results
      for (let i = 0; i < batchFiles.length; i++) {
        const file = batchFiles[i];
        try {
          const formDataToSend = new FormData();
          formDataToSend.append('file', file);
          formDataToSend.append('resource_type', batchFormData.resource_type);
          formDataToSend.append('is_public', batchFormData.is_public.toString());
          if (batchFormData.program_id) {
            formDataToSend.append('program_id', batchFormData.program_id);
          }
          
          const response = await fetch(ADMIN_URLS.RESOURCES, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
            },
            body: formDataToSend
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            
            // Handle specific error codes
            if (response.status === 413) {
              throw new Error('File size exceeds 10MB limit. Please select a smaller file.');
            } else if (response.status === 400) {
              throw new Error(errorData.error || 'Invalid file or data. Please check your input and try again.');
            } else if (response.status === 500) {
              throw new Error('Server error. Please try again later.');
            } else {
              throw new Error(errorData.error || `Server error (${response.status}). Please try again.`);
            }
          }
          
          const result = await response.json();
          results.push({ 
            file: file.name, 
            status: 'success', 
            message: 'Resource created successfully',
            id: result.id
          });
        } catch (err) {
          hasError = true;
          results.push({ 
            file: file.name, 
            status: 'error', 
            message: err.message || 'Failed to create resource'
          });
        }
      }
      
      setUploadResults(results);
      setShowResults(true);
      
      // If all uploads succeeded, redirect after a delay
      if (!hasError) {
        setTimeout(() => {
          navigate('/admin/resources');
        }, 3000);
      }
    } catch (err) {
      console.error("Error in batch upload:", err);
      setErrors({
        ...errors,
        submit: err.message || 'Failed to complete batch upload. Please check results.'
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Add New Resource</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard" className="admin-breadcrumb-link">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item">
            <Link to="/admin/resources" className="admin-breadcrumb-link">Resources</Link>
          </li>
          <li className="admin-breadcrumb-item active">
            <span className="admin-breadcrumb-link">Add Resource</span>
          </li>
        </ul>
      </div>
      
      {/* Upload mode toggle */}
      <div className="admin-card" style={{ marginBottom: '1rem' }}>
        <div className="admin-card-body">
          <div className="admin-upload-mode-toggle">
            <div className="admin-upload-toggle-label">Upload Mode:</div>
            <div className="admin-toggle-buttons">
              <button 
                type="button" 
                className={`admin-toggle-button ${!isBatchMode ? 'active' : ''}`}
                onClick={() => toggleMode()}
                disabled={isSaving}
              >
                <FontAwesomeIcon icon={faFile} className="admin-toggle-icon" />
                Single File
              </button>
              <button 
                type="button" 
                className={`admin-toggle-button ${isBatchMode ? 'active' : ''}`}
                onClick={() => toggleMode()}
                disabled={isSaving}
              >
                <FontAwesomeIcon icon={faLayerGroup} className="admin-toggle-icon" />
                Batch Upload
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {errors.submit && (
        <div className="admin-alert admin-alert-danger" style={{ marginBottom: '1rem' }}>
          {errors.submit}
        </div>
      )}
      
      {/* Results panel that appears after batch upload */}
      {showResults && (
        <div className="admin-card" style={{ marginBottom: '1rem' }}>
          <div className="admin-card-header">
            <h3 className="admin-card-title">Upload Results</h3>
          </div>
          <div className="admin-card-body">
            <div className="admin-upload-results">
              <div className="admin-upload-summary">
                <div className="admin-result-stat">
                  <span className="admin-result-label">Total:</span>
                  <span className="admin-result-value">{uploadResults.length}</span>
                </div>
                <div className="admin-result-stat admin-result-success">
                  <span className="admin-result-label">Successful:</span>
                  <span className="admin-result-value">
                    {uploadResults.filter(r => r.status === 'success').length}
                  </span>
                </div>
                <div className="admin-result-stat admin-result-error">
                  <span className="admin-result-label">Failed:</span>
                  <span className="admin-result-value">
                    {uploadResults.filter(r => r.status === 'error').length}
                  </span>
                </div>
              </div>
              
              <div className="admin-result-list">
                {uploadResults.map((result, index) => (
                  <div 
                    key={index} 
                    className={`admin-result-item ${result.status === 'success' ? 'admin-result-success' : 'admin-result-error'}`}
                  >
                    <div className="admin-result-file">
                      <FontAwesomeIcon 
                        icon={faFile} 
                        className="admin-result-icon" 
                      />
                      <span className="admin-result-filename">{result.file}</span>
                    </div>
                    <div className="admin-result-message">
                      {result.message}
                      {result.id && (
                        <Link 
                          to={`/admin/resources/${result.id}`}
                          className="admin-result-link"
                        >
                          View
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="admin-result-actions">
                <button 
                  type="button" 
                  className="admin-btn admin-btn-primary"
                  onClick={() => navigate('/admin/resources')}
                >
                  Go to Resources
                </button>
                <button 
                  type="button" 
                  className="admin-btn admin-btn-light"
                  onClick={() => {
                    setShowResults(false);
                    setBatchFiles([]);
                  }}
                >
                  Upload More Files
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {!showResults && (
        <div className="admin-card">
          <div className="admin-card-body">
            <form onSubmit={handleSubmit}>
              {/* Common fields for both modes */}
              <div className="admin-row">
                <div className="admin-col-12">
                  <div className="admin-form-group">
                    <label htmlFor="resource_type" className="admin-form-label">Resource Type *</label>
                    <select
                      id="resource_type"
                      name="resource_type"
                      value={isBatchMode ? batchFormData.resource_type : formData.resource_type}
                      onChange={handleInputChange}
                      className={`admin-form-control ${errors.resource_type ? 'admin-is-invalid' : ''}`}
                    >
                      <option value="">Select Resource Type</option>
                      <option value="document">Document</option>
                      <option value="presentation">Presentation</option>
                      <option value="video">Video</option>
                      <option value="link">External Link</option>
                    </select>
                    {errors.resource_type && <div className="admin-invalid-feedback">{errors.resource_type}</div>}
                  </div>
                </div>
              </div>
              
              <div className="admin-form-group">
                <label htmlFor="program_id" className="admin-form-label">Associated Program</label>
                <select
                  id="program_id"
                  name="program_id"
                  value={isBatchMode ? batchFormData.program_id : formData.program_id}
                  onChange={handleInputChange}
                  className="admin-form-control"
                  disabled={isLoading}
                >
                  <option value="">No Associated Program</option>
                  {programs.map(program => (
                    <option key={program.id} value={program.id}>{program.title}</option>
                  ))}
                </select>
                <small className="admin-form-text admin-text-muted">
                  Optional: Select a program to associate with this resource
                </small>
              </div>
              
              {/* Single upload mode */}
              {!isBatchMode && (
                <>
                  {/* Resource Source Section */}
                  <div className="admin-form-group">
                    <label htmlFor="url" className="admin-form-label">Resource URL</label>
                    <input
                      type="url"
                      id="url"
                      name="url"
                      value={formData.url}
                      onChange={handleInputChange}
                      className={`admin-form-control ${errors.url ? 'admin-is-invalid' : ''}`}
                      placeholder="https://example.com/resource"
                    />
                    <small className="admin-form-text admin-text-muted">
                      Enter a valid URL to link to an external resource
                    </small>
                    {errors.url && <div className="admin-invalid-feedback">{errors.url}</div>}
                  </div>
                  
                  <div className="admin-form-group">
                    <label className="admin-form-label">Or Upload File</label>
                    <div className="admin-file-upload-container">
                      <input 
                        type="file" 
                        id="file" 
                        onChange={handleFileChange}
                        className="admin-file-input"
                        accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif,.mp4,.mp3,.zip,.rar"
                      />
                      <label htmlFor="file" className="admin-file-upload-label">
                        <div className="admin-file-upload-content">
                          <FontAwesomeIcon icon={faUpload} className="admin-file-upload-icon" />
                          <div className="admin-file-upload-text">
                            <strong>Click to select file</strong> or drag & drop
                          </div>
                          <small className="admin-file-upload-help">
                            Supported: PDF, DOC, PPT, XLS, Images, Videos, Audio (Max: 10MB)
                          </small>
                        </div>
                      </label>
                      
                      {file && (
                        <div className="admin-file-selected">
                          <div className="admin-file-info">
                            <FontAwesomeIcon icon={faFile} className="admin-file-icon" />
                            <span className="admin-file-name">{file.name}</span>
                            <span className="admin-file-size">
                              ({(file.size / 1024 / 1024).toFixed(2)} MB)
                            </span>
                          </div>
                          <button 
                            type="button" 
                            onClick={() => setFile(null)}
                            className="admin-file-remove"
                            title="Remove file"
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </div>
                      )}
                      
                      {fileError && (
                        <div className="admin-invalid-feedback" style={{ display: 'block', marginTop: '0.5rem' }}>
                          {fileError}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
              
              {/* Batch upload mode */}
              {isBatchMode && (
                <div className="admin-form-group">
                  <label className="admin-form-label">Upload Multiple Files</label>
                  <div className="admin-batch-upload-container">
                    <input 
                      type="file" 
                      id="batchFiles" 
                      onChange={handleBatchFileChange}
                      className="admin-file-input"
                      accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif,.mp4,.mp3,.zip,.rar"
                      multiple
                    />
                    <label htmlFor="batchFiles" className="admin-file-upload-label">
                      <div className="admin-file-upload-content">
                        <FontAwesomeIcon icon={faUpload} className="admin-file-upload-icon" />
                        <div className="admin-file-upload-text">
                          <strong>Click to select multiple files</strong> or drag & drop
                        </div>
                        <small className="admin-file-upload-help">
                          Supported: PDF, DOC, PPT, XLS, Images, Videos, Audio (Max: 10MB each)
                        </small>
                      </div>
                    </label>
                  </div>
                  
                  {batchErrors.length > 0 && (
                    <div className="admin-batch-errors">
                      {batchErrors.map((error, index) => (
                        <div key={index} className="admin-invalid-feedback" style={{ display: 'block' }}>
                          {error.name}: {error.error}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {errors.files && (
                    <div className="admin-invalid-feedback" style={{ display: 'block', marginTop: '0.5rem' }}>
                      {errors.files}
                    </div>
                  )}
                  
                  {batchFiles.length > 0 && (
                    <div className="admin-batch-file-list">
                      <div className="admin-batch-file-header">
                        <span>Selected Files ({batchFiles.length})</span>
                      </div>
                      {batchFiles.map((file, index) => (
                        <div key={index} className="admin-batch-file-item">
                          <div className="admin-batch-file-info">
                            <FontAwesomeIcon icon={faFile} className="admin-file-icon" />
                            <span className="admin-batch-file-name">{file.name}</span>
                            <span className="admin-batch-file-size">
                              ({formatFileSize(file.size)})
                            </span>
                          </div>
                          <button 
                            type="button" 
                            onClick={() => removeBatchFile(index)}
                            className="admin-batch-file-remove"
                            title="Remove file"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Settings Section */}
              <div className="admin-form-group">
                <div className="admin-checkbox-wrapper">
                  <label className="admin-checkbox-label">
                    <input
                      type="checkbox"
                      name="is_public"
                      checked={isBatchMode ? batchFormData.is_public : formData.is_public}
                      onChange={handleInputChange}
                      className="admin-checkbox"
                    />
                    <span className="admin-checkbox-text">Make {isBatchMode ? 'all resources' : 'this resource'} public</span>
                  </label>
                  <small className="admin-form-text admin-text-muted">
                    <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '0.25rem' }} />
                    Public resources are visible to everyone, including non-members
                  </small>
                </div>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem' }}>
                <Link to="/admin/resources" className="admin-btn admin-btn-light">
                  <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: '0.5rem' }} />
                  Back to Resources
                </Link>
                
                <button 
                  type="submit"
                  className="admin-btn admin-btn-primary"
                  disabled={isSaving}
                >
                  <FontAwesomeIcon icon={faSave} style={{ marginRight: '0.5rem' }} />
                  {isSaving ? 'Creating...' : isBatchMode ? 'Upload All Files' : 'Create Resource'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      <style jsx>{`
        .admin-file-upload-container, .admin-batch-upload-container {
          border: 2px dashed #ced4da;
          border-radius: 8px;
          background-color: #f8f9fa;
          transition: all 0.3s ease;
          position: relative;
        }
        
        .admin-file-upload-container:hover, .admin-batch-upload-container:hover {
          border-color: #007bff;
          background-color: #f0f8ff;
        }
        
        .admin-file-input {
          position: absolute;
          width: 100%;
          height: 100%;
          opacity: 0;
          cursor: pointer;
        }
        
        .admin-file-upload-label {
          display: block;
          padding: 2rem;
          cursor: pointer;
          text-align: center;
        }
        
        .admin-file-upload-content {
          pointer-events: none;
        }
        
        .admin-file-upload-icon {
          font-size: 2rem;
          color: #6c757d;
          margin-bottom: 0.5rem;
          display: block;
        }
        
        .admin-file-upload-text {
          margin-bottom: 0.5rem;
          color: #495057;
        }
        
        .admin-file-upload-help {
          color: #6c757d;
        }
        
        .admin-file-selected, .admin-batch-file-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 1rem;
          padding: 0.75rem 1rem;
          background-color: #e9ecef;
          border-radius: 4px;
          border: 1px solid #ced4da;
        }
        
        .admin-file-info, .admin-batch-file-info {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .admin-file-icon {
          color: #007bff;
        }
        
        .admin-file-name, .admin-batch-file-name {
          font-weight: 500;
          color: #495057;
        }
        
        .admin-file-size, .admin-batch-file-size {
          color: #6c757d;
          font-size: 0.875rem;
        }
        
        .admin-file-remove, .admin-batch-file-remove {
          background: none;
          border: none;
          color: #dc3545;
          cursor: pointer;
          padding: 0.25rem;
          border-radius: 4px;
          transition: background-color 0.2s ease;
        }
        
        .admin-file-remove:hover, .admin-batch-file-remove:hover {
          background-color: #f8d7da;
        }
        
        .admin-checkbox-wrapper {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        
        .admin-checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          font-weight: 500;
        }
        
        .admin-checkbox {
          margin: 0;
        }
        
        .admin-checkbox-text {
          margin: 0;
        }
        
        .admin-alert {
          padding: 1rem;
          border-radius: 4px;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .admin-alert-danger {
          background-color: #f8d7da;
          border: 1px solid #f5c6cb;
          color: #721c24;
        }
        
        .admin-upload-mode-toggle {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        
        .admin-upload-toggle-label {
          font-weight: 500;
          color: #495057;
        }
        
        .admin-toggle-buttons {
          display: flex;
          border: 1px solid #ced4da;
          border-radius: 4px;
          overflow: hidden;
        }
        
        .admin-toggle-button {
          padding: 0.5rem 1rem;
          background-color: #f8f9fa;
          border: none;
          color: #495057;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s ease;
        }
        
        .admin-toggle-button:hover {
          background-color: #e9ecef;
        }
        
        .admin-toggle-button.active {
          background-color: #007bff;
          color: white;
        }
        
        .admin-toggle-button:first-child {
          border-right: 1px solid #ced4da;
        }
        
        .admin-toggle-icon {
          font-size: 0.875rem;
        }
        
        .admin-batch-file-list {
          margin-top: 1rem;
          border: 1px solid #ced4da;
          border-radius: 4px;
          overflow: hidden;
        }
        
        .admin-batch-file-header {
          padding: 0.75rem 1rem;
          background-color: #f8f9fa;
          border-bottom: 1px solid #ced4da;
          font-weight: 500;
          color: #495057;
        }
        
        .admin-batch-file-item {
          margin-top: 0;
          border: none;
          border-radius: 0;
          border-bottom: 1px solid #ced4da;
        }
        
        .admin-batch-file-item:last-child {
          border-bottom: none;
        }
        
        .admin-upload-results {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        
        .admin-upload-summary {
          display: flex;
          gap: 2rem;
          padding: 1rem;
          background-color: #f8f9fa;
          border-radius: 4px;
          border: 1px solid #ced4da;
        }
        
        .admin-result-stat {
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        
        .admin-result-label {
          font-weight: 500;
          color: #495057;
          font-size: 0.875rem;
        }
        
        .admin-result-value {
          font-size: 1.5rem;
          font-weight: bold;
        }
        
        .admin-result-success .admin-result-value {
          color: #28a745;
        }
        
        .admin-result-error .admin-result-value {
          color: #dc3545;
        }
        
        .admin-result-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          max-height: 300px;
          overflow-y: auto;
        }
        
        .admin-result-item {
          display: flex;
          justify-content: space-between;
          padding: 0.75rem 1rem;
          border-radius: 4px;
          background-color: #f8f9fa;
          border: 1px solid #ced4da;
          align-items: center;
        }
        
        .admin-result-item.admin-result-success {
          border-left: 4px solid #28a745;
        }
        
        .admin-result-item.admin-result-error {
          border-left: 4px solid #dc3545;
        }
        
        .admin-result-file {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .admin-result-icon {
          color: #6c757d;
        }
        
        .admin-result-filename {
          font-weight: 500;
          color: #495057;
        }
        
        .admin-result-message {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        
        .admin-result-link {
          padding: 0.25rem 0.75rem;
          background-color: #007bff;
          color: white;
          text-decoration: none;
          border-radius: 4px;
          font-size: 0.875rem;
        }
        
        .admin-result-actions {
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          margin-top: 1rem;
        }
      `}</style>
    </AdminLayout>
  );
}

export default AddResource;
